// Small entrypoint for the banner studio: the editor, renderer and fonts load
// only on intent. Same lifecycle as the sleeve loader; only the host element
// and module names differ.
function studioModuleURL(value, filename) {
  const url = new URL(value, document.baseURI);
  const local =
    url.origin === location.origin && ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);
  const trusted = url.origin === location.origin || url.hostname === 'cdn.shopify.com';
  if (
    (!local && (url.protocol !== 'https:' || !trusted)) ||
    url.username ||
    url.password ||
    url.hash ||
    !url.pathname.endsWith('/assets/' + filename) ||
    /%|\\/.test(url.pathname)
  )
    throw new Error('invalid_studio_module');
  return url;
}
let pending,
  attempt = 0,
  opening = 0;
const host = () => document.querySelector('nwp-banner-studio');
const configuration = () =>
  JSON.parse(host().querySelector('[data-nwp-studio-config]').textContent);
const frame = () => new Promise((resolve) => requestAnimationFrame(resolve));
function load() {
  if (customElements.get('nwp-banner-studio')) return Promise.resolve(host());
  if (!pending) {
    const url = studioModuleURL(configuration().assets.runtime, 'nwp-banner-studio.js');
    if (attempt) url.searchParams.set('studio_retry', String(attempt));
    // Validated HTTPS Shopify theme asset; never an HTML or user-controlled module URL.
    // eslint-disable-next-line no-unsanitized/method
    pending = import(url.href)
      .then(() => customElements.whenDefined('nwp-banner-studio'))
      .then(() => host())
      .catch((error) => {
        pending = null;
        attempt++;
        throw error;
      });
  }
  return pending;
}
async function launch(button) {
  const ticket = ++opening,
    config = configuration(),
    root = document.createElement('div');
  root.className = 'nwp-studio nwp-banner-studio-root is-loading';
  root.dataset.studioBoot = '';
  root.setAttribute('aria-busy', 'true');
  const offset = () =>
    root.style.setProperty(
      '--nwp-studio-header-offset',
      `${Math.max(0, Math.min(innerHeight / 3, document.querySelector('.shopify-section--header')?.getBoundingClientRect().bottom || 0))}px`,
    );
  offset();
  window.addEventListener('resize', offset);
  const loader = host()
    .querySelector('[data-nwp-studio-loader]')
    .content.firstElementChild.cloneNode(true);
  loader.hidden = false;
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'nwp-studio__btn nwp-studio__loading-close';
  close.textContent = config.strings.close;
  const cleanup = () => {
    window.removeEventListener('resize', offset);
    root.remove();
  };
  const cancel = () => {
    opening++;
    cleanup();
    button.focus();
  };
  close.addEventListener('click', cancel);
  root.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      cancel();
    }
  });
  loader.append(close);
  root.append(loader);
  document.body.append(root);
  close.focus();
  try {
    await frame();
    await frame();
    const studio = await load();
    if (ticket !== opening) return;
    const ready = studio.open(button);
    cleanup();
    await ready;
  } catch {
    if (ticket !== opening) return;
    root.removeAttribute('aria-busy');
    const status = loader.querySelector('[data-nwp-customizer-loader-copy]');
    status.textContent = config.strings.storage_down;
    status.setAttribute('role', 'alert');
    status.removeAttribute('aria-hidden');
    const retry = loader.querySelector('[data-nwp-customizer-loader-retry]');
    retry.hidden = false;
    retry.addEventListener(
      'click',
      () => {
        cleanup();
        launch(button);
      },
      { once: true },
    );
    retry.focus();
  }
}
document.addEventListener(
  'click',
  (event) => {
    const button = event.target.closest?.('[data-nwp-studio-open]');
    if (!button || customElements.get('nwp-banner-studio')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    launch(button);
  },
  true,
);
for (const type of ['pointerover', 'focusin'])
  document.addEventListener(
    type,
    (event) => {
      const button = event.target.closest?.('[data-nwp-studio-open]');
      if (!button) return;
      // Touch browsers synthesize hover before a tap. Initializing the editor
      // during that hover can change the page and consume the first activation.
      if (
        event.type === 'pointerover' &&
        (event.pointerType !== 'mouse' || !matchMedia('(hover: hover) and (pointer: fine)').matches)
      )
        return;
      if (event.type === 'focusin' && !button.matches(':focus-visible')) return;
      load()
        .then((studio) => studio.preload())
        .catch(() => {});
    },
    { passive: true },
  );
